import React, { useEffect, useRef } from 'react';
import Button from '../components/ui/Button';
import { CheckIcon, AwardIcon, TargetIcon, HeartIcon, BuildingIcon, PenToolIcon, RefreshCwIcon, ClipboardListIcon, HomeIcon, KeyIcon, WrenchIcon, CompassIcon } from 'lucide-react';
const About = () => {
  const sectionRefs = {
    company: useRef(null),
    mission: useRef(null),
    services: useRef(null),
    team: useRef(null),
    why: useRef(null)
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Make sure we're accessing the current property correctly
          if (entry.target.classList.contains('section-hidden')) {
            entry.target.classList.remove('section-hidden');
            entry.target.classList.add('animate-fadeIn');
          }
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    // Make sure we're observing the correct elements
    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) {
        // Add a class we can check for
        ref.current.classList.add('section-hidden');
        observer.observe(ref.current);
      }
    });
    return () => {
      Object.values(sectionRefs).forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);
  return <div className="w-full pt-20" data-id="element-170">
      {/* Hero Section */}
      <section className="relative py-20 bg-gray-900" data-id="element-171">
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-172">
          <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80" alt="Construction team" className="w-full h-full object-cover" data-id="element-173" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center" data-id="element-174">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fadeInDown" data-id="element-175">
            About Us
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fadeInUp" data-id="element-176">
            Building Futures, Real Estate, & Investment Solutions
          </p>
        </div>
      </section>
      {/* Company Overview */}
      <section className="py-20" ref={sectionRefs.company} data-id="element-177">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-178">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-179">
            <div data-id="element-180">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-6" data-id="element-181">
                Our Company
              </h2>
              <p className="text-lg text-gray-700 mb-4" data-id="element-182">
                Socrates & Richard is a dynamic construction company with over a
                decade experience offering comprehensive solutions in
                construction, real estate, engineering, and property
                development. Our services cover every stage of the project —
                from architectural design and structural planning to finishing,
                furnishing, and facility management.
              </p>
              <p className="text-lg text-gray-700 mb-4" data-id="element-183">
                As a privately owned firm, our mission is to deliver authentic,
                high-quality building and real estate services that exceed
                client expectations. Backed by the expertise and experience of
                our skilled professionals, we ensure excellence, efficiency, and
                innovation in every project.
              </p>
              <p className="text-lg text-gray-700 mb-4" data-id="element-184">
                With branches in Abuja, Port Harcourt, Lagos, and Edo State,
                Socrates & Richard continues to expand its footprint across
                Nigeria, building trust and transforming spaces that stand the
                test of time.
              </p>
              <div className="mt-8" data-id="element-185">
                <Button to="/projects" variant="primary" data-id="element-186">
                  Explore Projects
                </Button>
              </div>
            </div>
            <div className="transform transition-transform duration-700 hover:scale-105" data-id="element-187">
              <img src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Construction site" className="rounded-lg shadow-xl w-full" data-id="element-188" />
            </div>
          </div>
        </div>
      </section>
      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50" ref={sectionRefs.mission} data-id="element-189">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-190">
          <div className="text-center mb-16" data-id="element-191">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-4" data-id="element-192">
              Mission, Vision & Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-193">
              The principles that guide our work and relationships
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-194">
            <div className="bg-white rounded-lg shadow-md p-8 text-center transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl" data-id="element-195">
              <div className="inline-block p-4 bg-[#e9f5cc] rounded-full text-[#95c11f] mb-6" data-id="element-196">
                <TargetIcon size={32} data-id="element-197" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2e1a] mb-4" data-id="element-198">
                Our Mission
              </h3>
              <p className="text-gray-700" data-id="element-199">
                We shall ensure that our customer's specific desirable
                requirements are exceptionally met using our modernized
                equipments, highly skilled employees, innovation and the finest
                quality materials to leave a lasting reputable impact.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-8 text-center transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl" data-id="element-200">
              <div className="inline-block p-4 bg-[#e9f5cc] rounded-full text-[#95c11f] mb-6" data-id="element-201">
                <AwardIcon size={32} data-id="element-202" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2e1a] mb-4" data-id="element-203">
                Our Vision
              </h3>
              <p className="text-gray-700" data-id="element-204">
                We are passionate about being the leading and most acknowledged
                company highly acclaimed for delivering distinctively
                tailor-made, unique and authentic construction and real estate
                services to our customers in Nigeria and across the globe.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-8 text-center transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl" data-id="element-205">
              <div className="inline-block p-4 bg-[#e9f5cc] rounded-full text-[#95c11f] mb-6" data-id="element-206">
                <HeartIcon size={32} data-id="element-207" />
              </div>
              <h3 className="text-2xl font-bold text-[#1a2e1a] mb-4" data-id="element-208">
                Our Core Values
              </h3>
              <p className="text-gray-700" data-id="element-209">
                Integrity | Professionalism | Excellence | Innovation | Customer
                Satisfaction
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Services Section */}
      <section className="py-20" ref={sectionRefs.services} data-id="element-210">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-211">
          <div className="text-center mb-16" data-id="element-212">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-4" data-id="element-213">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-214">
              Comprehensive construction and real estate solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-215">
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-216">
              <div className="text-[#95c11f] mb-4" data-id="element-217">
                <BuildingIcon size={32} data-id="element-218" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-219">
                Construction Services
              </h3>
              <p className="text-gray-700" data-id="element-220">
                Quality work at Socrates & Richard Company Limited is
                accomplished through careful planning, dedication, skilled and
                superior execution by our well trained and experienced team. We
                are committed to building high quality projects no matter what
                the size or scope is, it will stand out for generations to come.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-221">
              <div className="text-[#95c11f] mb-4" data-id="element-222">
                <PenToolIcon size={32} data-id="element-223" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-224">
                Design & Building Services
              </h3>
              <p className="text-gray-700" data-id="element-225">
                Design and Building is one of the most proficient and important
                part of our construction company. We have a duty as a
                construction company to deliver accurate, perfect designs and
                building services as we are viewed based on our services
                provided in the industry. We have developed significant
                relationship with clients, engineering firms and other
                contractors to address design and constructing issues.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-226">
              <div className="text-[#95c11f] mb-4" data-id="element-227">
                <RefreshCwIcon size={32} data-id="element-228" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-229">
                Rehabilitation & Renovation Services
              </h3>
              <p className="text-gray-700" data-id="element-230">
                Our Company has been privileged to work in some of the most
                beautiful areas of the country. Maintaining and protecting a
                healthy environment and properties is essential to us. We are
                committed to protecting the environment by carefully planning of
                our work with the goal of sustainable environment in mind and
                the use of best management practices.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-231">
              <div className="text-[#95c11f] mb-4" data-id="element-232">
                <ClipboardListIcon size={32} data-id="element-233" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-234">
                Project Management
              </h3>
              <p className="text-gray-700" data-id="element-235">
                We ensure the success of projects from the scoping and
                programming stages all the way through design, construction,
                commission, close out (commissioning) and moving in. Each
                project is carefully designed and planned for execution and a
                successful outcome that meets the project goals.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-236">
              <div className="text-[#95c11f] mb-4" data-id="element-237">
                <HomeIcon size={32} data-id="element-238" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-239">
                Property Development
              </h3>
              <p className="text-gray-700" data-id="element-240">
                As the industry increasingly shifts towards the provision of
                more specialist services demanded by customers, our goal is to
                within the best of our capacity, to continuously provide our
                clients with their required specifications. This also includes
                sales or lease of funded properties on completion.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-241">
              <div className="text-[#95c11f] mb-4" data-id="element-242">
                <KeyIcon size={32} data-id="element-243" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-244">
                Real Estate Services
              </h3>
              <p className="text-gray-700" data-id="element-245">
                Our Company works with commercial and residential builders, real
                estate and subsidized housing developers, owners and operators,
                heavy construction companies, specialty engineers and federal
                government contractors to meet and exceed client's expectations.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-246">
              <div className="text-[#95c11f] mb-4" data-id="element-247">
                <WrenchIcon size={32} data-id="element-248" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-249">
                Property Management Services
              </h3>
              <p className="text-gray-700" data-id="element-250">
                We offer continuous, uninterrupted coverage for maintenance and
                management responsibilities for building facilities. We provide
                all necessary labour, materials, equipments and support to
                ensure the successful implementation of all requirements listed
                in our agreements.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 transition-all duration-300 hover:shadow-xl" data-id="element-251">
              <div className="text-[#95c11f] mb-4" data-id="element-252">
                <CompassIcon size={32} data-id="element-253" />
              </div>
              <h3 className="text-xl font-bold text-[#1a2e1a] mb-3" data-id="element-254">
                General Engineering Services
              </h3>
              <p className="text-gray-700" data-id="element-255">
                Specialized and well-trained engineers are there to help, guide,
                and oversee our construction project. This also includes
                construction of roads, bridges, pipeline installation, sewage
                systems, railway roads and others. We provide all necessary
                labor, material, equipments and support.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Team Section */}
      <section className="py-20 bg-gray-50" ref={sectionRefs.team} data-id="element-256">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-257">
          <div className="text-center mb-16" data-id="element-258">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-4" data-id="element-259">
              Our Leadership
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-260">
              Meet the experienced professionals who lead our company
            </p>
          </div>
          <div className="max-w-3xl mx-auto" data-id="element-261">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-262">
              <div className="md:flex" data-id="element-263">
                <div className="md:flex-shrink-0" data-id="element-264">
                  <img className="h-48 w-full object-cover md:w-48" src="https://uploadthingy.s3.us-west-1.amazonaws.com/1JBxna3GB2qfp9rspSfNkv/unnamed_%284%29.jpg" alt="Richard Edebiri - CEO" data-id="element-265" />
                </div>
                <div className="p-8" data-id="element-266">
                  <div className="uppercase tracking-wide text-sm text-[#95c11f] font-semibold" data-id="element-267">
                    Chairman/CEO
                  </div>
                  <h3 className="mt-1 text-2xl font-bold text-[#1a2e1a]" data-id="element-268">
                    Richard Edebiri
                  </h3>
                  <p className="mt-4 text-gray-700" data-id="element-269">
                    Richard Edebiri currently serves as the Chairman/Chief
                    Executive Officer of Socrates and Richard Company Ltd. Prior
                    to his current role, he gathered valuable experience as the
                    Project Manager with Greenrich Nigeria Company.
                  </p>
                  <p className="mt-2 text-gray-700" data-id="element-270">
                    Richard has a HND in Building Technology from Federal
                    Polytechnic, Nasarawa, a Bachelors degree in Material and
                    Production Engineering from Ambrose Alli University, Ekpoma
                    and a Masters in Science Degree (M.Sc) in Project Management
                    from the prestigious Robert Gordon University, Aberdeen,
                    Scotland - UK.
                  </p>
                  <p className="mt-2 text-gray-700" data-id="element-271">
                    He is an experienced manager, team leader and motivator with
                    great interpersonal skills, team building skills and has the
                    ability to deliver projects under tough circumstances.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Why Choose Us */}
      <section className="py-20" ref={sectionRefs.why} data-id="element-272">
        <div className="container mx-auto px-4 md:px-6 transition-opacity duration-1000" data-id="element-273">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-274">
            <div className="transform transition-transform duration-700 hover:scale-105" data-id="element-275">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ksFwscNdhK6ddZHmFijTWo/image_.jpg" alt="Modern residential complex" className="rounded-lg shadow-xl w-full" data-id="element-276" />
            </div>
            <div data-id="element-277">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2e1a] mb-6" data-id="element-278">
                Why Choose Us
              </h2>
              <ul className="space-y-4" data-id="element-279">
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-280">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-281">
                    <CheckIcon size={20} data-id="element-282" />
                  </div>
                  <div data-id="element-283">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-284">
                      Timely & Efficient Service
                    </h3>
                    <p className="text-gray-700" data-id="element-285">
                      We render timely and efficient services to meet our
                      clients' deadlines.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-286">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-287">
                    <CheckIcon size={20} data-id="element-288" />
                  </div>
                  <div data-id="element-289">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-290">
                      Skilled & Efficient Staff
                    </h3>
                    <p className="text-gray-700" data-id="element-291">
                      We work with highly skilled and efficient staff to render
                      excellent services.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-292">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-293">
                    <CheckIcon size={20} data-id="element-294" />
                  </div>
                  <div data-id="element-295">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-296">
                      Modern Technology
                    </h3>
                    <p className="text-gray-700" data-id="element-297">
                      We make use of modern technology to deliver high quality
                      real estate and construction services.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-298">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-299">
                    <CheckIcon size={20} data-id="element-300" />
                  </div>
                  <div data-id="element-301">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-302">
                      Integrity Based Practice
                    </h3>
                    <p className="text-gray-700" data-id="element-303">
                      We run an integrity based practice and give customers
                      value for their money.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-304">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-305">
                    <CheckIcon size={20} data-id="element-306" />
                  </div>
                  <div data-id="element-307">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-308">
                      Safety Focused
                    </h3>
                    <p className="text-gray-700" data-id="element-309">
                      We are dedicated to the safety of each and every one of
                      our workers, as they are the greatest assets of the
                      company.
                    </p>
                  </div>
                </li>
                <li className="flex items-start transform transition-all duration-500 hover:-translate-x-2" data-id="element-310">
                  <div className="bg-[#e9f5cc] p-1 rounded-full text-[#95c11f] mr-4 mt-1" data-id="element-311">
                    <CheckIcon size={20} data-id="element-312" />
                  </div>
                  <div data-id="element-313">
                    <h3 className="text-xl font-bold text-[#1a2e1a] mb-1" data-id="element-314">
                      Socially Responsible
                    </h3>
                    <p className="text-gray-700" data-id="element-315">
                      We are a socially responsible and responsive organisation
                      that seeks to give back to society.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default About;